import { MonthDatePicker } from "../calendar/MonthDatePicker.ts";
import * as i18n from "../i18n.ts";
import * as Html from "../util/Html.ts";
import Icons from "../svg/Icons.ts";
import { PIECE } from "../lib/ingredientParser.ts";
import { clampQuantity, formatUnitName, localUnit, QUANTITY_MAX, QUANTITY_STEP, rememberUnit, unitChoices } from "../lib/unitFormat.ts";
import * as modal from "../util/modal.ts";
import type { ActionMenuContent } from "./action-menu-registry.ts";

/**
 * Custom contents shipped with the widget. Anything else can be injected the same way: build the
 * markup, wire it in init(). See ActionMenuContent.
 */

export function datePicker(options :{
  date?:Date,
  firstDayOfWeek?:number,
  showYear?:boolean,
  onpickdate:(date:Date,showYear:boolean)=>void
}) :ActionMenuContent{
  return {
    html:'<div class="MonthDatePicker"></div>',
    init(root,close){
      new MonthDatePicker(root.querySelector<HTMLElement>(".MonthDatePicker"),{
        date:options.date,
        firstDayOfWeek:options.firstDayOfWeek,
        showWeek:false,
        showYear:options.showYear,
        onpickdate(date,showYear){
          close()
          options.onpickdate(date,showYear)
        }
      })
    }
  }
}

/**
 * Default destination of a picked emoji: the opener's own field when it has one (emoji button next
 * to a hidden input), the surrounding form's text field otherwise (emoji button of a message input).
 */
export function emojiIntoField(opener :HTMLElement){
  return function(emoji :string){
    const input = opener.querySelector('input');
    if(input){
      const emojiNode = opener.querySelector('emoji');
      if(emojiNode) emojiNode.innerHTML = emoji;
      input.value = emoji;
      input.dispatchEvent(new Event('change'));
    }else{
      const targetNode = opener.closest("form").querySelector('textarea,*[name="text"]') as HTMLInputElement|HTMLTextAreaElement;
      targetNode.value = targetNode.value + emoji;
      targetNode.focus();
    }
  }
}

export function emojiPicker(emojis :string[],onpick :(emoji:string)=>void) :ActionMenuContent{
  return {
    html:`<div style="width:204px;padding:8px;line-height:0">${
      emojis.map(emoji=>`<emoji style="cursor:pointer;margin:2px;">${emoji}</emoji>`).join('')
    }</div>`,
    init(root){
      root.querySelectorAll('emoji').forEach(emojiEl=>{
        emojiEl.addEventListener('click',()=>onpick(emojiEl.innerHTML))
      })
    }
  }
}

/**
 * Stepper + unit chips. Nothing is notified while editing: read value() once the picker is gone,
 * from the menu's onCancel or from the modal hosting it. A quantity of 0 means "no quantity".
 */
export function quantityPicker(options :{
  quantity?:number,
  unit?:string
}) :ActionMenuContent&{value:()=>{quantity:number,unit:string}}{
  let
    quantity = options.quantity>0?options.quantity:0,
    //localUnit(): the chips speak the language, an item written by another client may carry a unitKey
    unit = localUnit(options.unit) || PIECE;

  const units = unitChoices(unit);

  return {
    value:()=>({quantity,unit}),
    html:`<div class="quantity-picker">
  <div class="picker-title">${Html.escape(i18n.get('task_details.editquantity'))}</div>
  <flexrow class="stepper">
    <icon data-step="-1">${Icons.ic_minus}</icon>
    <input type="number" min="0" max="${QUANTITY_MAX}" step="${QUANTITY_STEP}" inputmode="decimal" class="flexauto" value="${quantity}">
    <icon data-step="1">${Icons.ic_add_plus}</icon>
  </flexrow>
  <div class="units">${
    units.map(one=>`<span data-unit="${Html.escape(one)}">${Html.escape(formatUnitName(one))}</span>`).join('')
  }<span class="addunit">+</span></div>
</div>`,
    init(root){
      root.classList.remove('selectable')
      const
        input = root.querySelector('.quantity-picker input') as HTMLInputElement,
        minus = root.querySelector('.quantity-picker [data-step="-1"]'),
        plus = root.querySelector('.quantity-picker [data-step="1"]'),
        addChip = root.querySelector('.quantity-picker .addunit');

      function majUI(){
        input.value = String(quantity);
        minus.classList.toggle('disabled',quantity<=0);
        plus.classList.toggle('disabled',quantity>=QUANTITY_MAX);
        root.querySelectorAll('.quantity-picker [data-unit]').forEach(chip=>{
          chip.classList.toggle('selected',chip.getAttribute('data-unit')===unit);
        })
      }

      function pick(name :string){
        unit = name;
        //picking a unit alone means one of it: "bag" is 1 bag, never 0
        if(quantity===0) quantity = 1;
        majUI();
      }

      function wireChip(chip :Element){
        chip.addEventListener('click',()=>pick(chip.getAttribute('data-unit')))
      }

      function chipOf(name :string){
        return Array.from(root.querySelectorAll('.quantity-picker [data-unit]'))
          .find(chip=>chip.getAttribute('data-unit')===name);
      }

      root.querySelectorAll('.quantity-picker [data-step]').forEach(button=>{
        button.addEventListener('click',()=>{
          quantity = clampQuantity(quantity + Number(button.getAttribute('data-step')));
          majUI();
        })
      })
      root.querySelectorAll('.quantity-picker [data-unit]').forEach(wireChip)
      //any unit is allowed, the chips are only the usual suspects
      addChip.addEventListener('click',async ()=>{
        const typed = (await modal.askText({
          title:'task_details.addunittitle',
          placeholder:'task_details.addunitplaceholder',
          buttonName:'actions.save',
          message:''
        }))?.trim();

        if(!typed) return;
        //"grams" or "cuillère à soupe" is a unit we already know: reuse its chip and its translations
        const name = rememberUnit(typed,i18n.getShortLanguage());

        if(!chipOf(name)){
          addChip.insertAdjacentHTML('beforebegin',`<span data-unit="${Html.escape(name)}">${Html.escape(formatUnitName(name))}</span>`);
          wireChip(addChip.previousElementSibling);
        }
        pick(name);
      })
      //the number input takes both decimal separators, whatever the keyboard, and hands back a plain
      //number — NaN when the field is left empty, which clamps down to "no quantity"
      input.addEventListener('change',()=>{
        quantity = clampQuantity(input.valueAsNumber);
        majUI();
      })
      //the menu is injected next to its opener, which may sit inside a form: enter validates the
      //quantity, it must not submit that form
      input.addEventListener('keydown',event=>{
        if(event.key==='Enter'){
          event.preventDefault();
          input.dispatchEvent(new Event('change'));
        }
      })

      majUI();
    }
  }
}
